<template>
  <div class="attendee-list">
    <div class="row" v-for="(attendee, a) in sortedAttendees" :key="`attendee-${a}`">
      <GamAttendeeRow
        class="attendee"
        :player="attendee"
        :is-organizer="attendee?.id === gambit?.ownerId"
        :is-handshake="canCreateHandshakeWith(attendee) || canReturnHandshakeTo(attendee)"
        :show-check-in="activeCheckIns?.some((c) => c.id === attendee.id)"
        :chess-set-count="attendee.chessSetCount ?? 0"
        :show-coach="isLesson"
        :show-streamer="isStreamed"
        show-unpaired-badge
        :order="a"
        :is-row-action="canCreateHandshakeWith(attendee) || canReturnHandshakeTo(attendee)"
        @select="handleClick(attendee)"
      />
      <template v-if="canCreateHandshakeWith(attendee) || canReturnHandshakeTo(attendee)">
        <HandshakeButton
          class="handshake-button"
          :cta-label="ctaLabelFor(attendee.id)"
          @click="handleClick(attendee)"
          :is-pending="!!getPendingHandshakeFor(attendee.id)"
          :is-completed="false"
          :is-recommendation="liveTap.cta(attendee.id) === 'open'"
          :is-unplayed="liveTap.isUnplayed(attendee.id)"
          :position="liveTap.recommendation(attendee.id).position"
          :position-on="liveTap.recommendation(attendee.id).state === 'open'"
          :responder-id="getHandshakeFor(attendee.id)?.responderId!"
        />
      </template>
    </div>

    <div class="row invitee" v-for="(invitee, i) in invitees" :key="`invitee-${i}`">
      <GamInviteeRow v-if="invitee" class="attendee" :invitee="invitee" :order="i" />
    </div>

    <GamConfirmModal
      v-if="cancelHandshakeId"
      :title="localize('handshake.cancel.title')"
      :keep-label="localize('handshake.cancel.dismiss')"
      :destructive-label="localize('handshake.cancel.confirm')"
      @close="cancelHandshakeId = null"
      @keep="cancelHandshakeId = null"
      @confirm="handleConfirmCancelHandshake"
    />
  </div>
</template>

<script lang="ts">
export interface AttendeeCheckInDto {
  userId: string;
  start: string;
  end: string | null;
  locationId: string | null;
  gambitId: string | null;
}

interface HandshakePlayer {
  id: string;
  username: string;
  profileImageBlobHash: string | null;
}

interface Handshake {
  id: string;
  initiatorId: string | null;
  initiator: HandshakePlayer | null;
  responderId: string | null;
  responder: HandshakePlayer | null;
  initiatorImageBlobHash: string | null;
  responderImageBlobHash: string | null;
  isConfirmed: boolean;
  createdAt: string;
  updatedAt: string;
  timezone: string;
}

interface AttendeeListGambitDto {
  id: string;
  start: string;
  end: string;
  ownerId: string | null;
  gambitType: GambitType[];
  gambitCategory: GambitCategory;
  handshakes?: Handshake[];
  pendingHandshakes?: Handshake[];
}

interface AttendeeListLocationDto {
  id: string;
  name: string;
  handshakes?: HandshakeDto[];
  pendingHandshakes?: HandshakeDto[];
  isCheckedIn: boolean;
}

export interface CheckInDto {
  userId: string;
  start: string;
  end: string | null;
}

export interface CheckInsListProps {
  gambit?: AttendeeListGambitDto;
  location: AttendeeListLocationDto;

  activeCheckIns: VisitorDto[];
  invitees?: Array<InviteeDto>;

  user: { id: string } | null;

  handshakes: HandshakeDto[];
  pendingHandshakes: HandshakeDto[];
}
</script>

<script setup lang="ts">
import { GambitCategory, GambitType } from '@/core/data/gambit/gambit.type';
import type { HandshakeDto } from '@/core/data/handshake/handshake.interface';
import type { VisitorDto } from '@/core/data/location/location.interface';
import type { AttendeeDto, InviteeDto } from '@/core/data/user/user.interface';
import { gambits, localize } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import GamAttendeeRow from '@/views/components/gam-player/GamAttendeeRow.vue';
import GamInviteeRow from '@/views/components/gam-player/GamInviteeRow.vue';
import GamConfirmModal from '@/views/components/GamConfirmModal.vue';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import { useConfetti } from '@/views/composables/hooks/useConfetti';
import { useCtaLabels } from '@/views/composables/hooks/useCtaLabels';
import { useLiveHandshakeTap } from '@/views/composables/hooks/useLiveHandshakeTap';
import {
  isHandshakeModalOpen,
  markHandshakeModalClosed,
  markHandshakeModalOpen,
  markHandshakeShown,
  wasHandshakeShown,
} from '@/views/main/handshakes/handshake-modal-queue';
import HandshakeButton from '@/views/main/handshakes/HandshakeButton.vue';
import HandshakeModal from '@/views/main/handshakes/HandshakeModal.vue';
import { celebrateNewlyConfirmedHandshakes } from '@/views/main/handshakes/utils/handshake-celebration';
import { isHandshakeLive } from '@/views/main/handshakes/utils/handshake-game';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { useModal } from 'vue-final-modal';

const props = defineProps<CheckInsListProps>();
const listEmit = defineEmits([GamComponentsEmits.UPDATE]);

const authStore = useAuthStore();
const { user: authUser } = storeToRefs(authStore);

const checkedInAttendees = computed(() => {
  const attendeesCopy = (props.activeCheckIns || []).concat([]);

  return attendeesCopy.filter((a) => a.isCheckedIn).sort((a) => (a.id === authUser.value?.id ? 1 : -1));
});

const nonCheckedInAttendees = computed(() => {
  const attendeesCopy = (props.activeCheckIns || []).concat([]);

  return attendeesCopy.filter((a) => !a.isCheckedIn).sort((a) => (a.id === authUser.value?.id ? -1 : 1));
});

const sortedAttendees = computed(() => {
  return [...checkedInAttendees.value, ...nonCheckedInAttendees.value];
});

const getHandshakeFor = (playerId: string) => {
  const now = new Date();

  const playerHandshakes = props.handshakes
    .filter(
      (h) =>
        isHandshakeLive(h.createdAt, now) &&
        ((h.initiatorId === authUser.value?.id && h.responderId === playerId) ||
          (h.initiatorId === playerId && h.responderId === authUser.value?.id)),
    )
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return playerHandshakes[0] || null;
};

const getPendingHandshakeFor = (playerId: string) => {
  const playerHandshakes = props.pendingHandshakes
    .filter(
      (h) =>
        (h.initiatorId === authUser.value?.id && h.responderId === playerId) ||
        (h.initiatorId === playerId && h.responderId === authUser.value?.id),
    )
    .sort((a, b) => {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

  return playerHandshakes[0] || null;
};

const liveTap = useLiveHandshakeTap(() => ({ locationId: props.location.id }));

const { isLearning } = useCtaLabels();

/** What the button does, in words, while the player is still new to it. */
const ctaLabelFor = (playerId: string): string | undefined => {
  if (!isLearning.value || liveTap.cta(playerId) === 'open') return undefined;

  if (getPendingHandshakeFor(playerId)?.initiatorId === authUser.value?.id) return undefined;

  return localize('live.banner.handshake');
};

const handshakeAttendee = ref<{ id: string; username: string; profileImageBlobHash: string | null } | null>(null);
const currentHandshake = ref<HandshakeDto | null>(null);
const currentGambitId = ref<string | null>(props.gambit?.id || null);
const currentLocationId = ref<string | null>(props.location?.id || null);
const showTimestamp = ref<boolean>(true);

const isModalOpen = ref(false);

const setModalOpen = (open: boolean) => {
  if (isModalOpen.value === open) return;
  isModalOpen.value = open;
  if (open) markHandshakeModalOpen();
  else markHandshakeModalClosed();
};

onUnmounted(() => setModalOpen(false));

const { open: openHandshakeModal, close } = useModal({
  component: HandshakeModal,
  attrs: {
    player: handshakeAttendee,
    handshake: currentHandshake,
    gambitId: currentGambitId,
    locationId: currentLocationId,
    showTimestamp: showTimestamp,
    onClose: (options?: { refresh?: boolean }) => {
      setModalOpen(false);
      if (currentHandshake.value) {
        markHandshakeShown(currentHandshake.value.id);
      }
      currentHandshake.value = null;
      close();
      if (options?.refresh) {
        listEmit(GamComponentsEmits.UPDATE);
      }
    },
  },
});

const cancelHandshakeId = ref<string | null>(null);

const handleClick = async (attendee: AttendeeDto) => {
  const pending = getPendingHandshakeFor(attendee.id);
  if (pending && pending.initiatorId === authUser.value?.id) {
    cancelHandshakeId.value = pending.id;
    return;
  }

  if (await liveTap.tap(attendee.id)) return;

  handshakeAttendee.value = attendee;
  currentHandshake.value = getHandshakeFor(attendee.id) || getPendingHandshakeFor(attendee.id) || null;
  showTimestamp.value = !!getHandshakeFor(attendee.id);

  setModalOpen(true);
  nextTick(() => {
    openHandshakeModal();
  });
};

const handleConfirmCancelHandshake = async () => {
  if (!cancelHandshakeId.value) return;
  await gambits.userService.deleteHandshake(cancelHandshakeId.value);
  cancelHandshakeId.value = null;
  listEmit(GamComponentsEmits.UPDATE);
};

const pendingHandshakes = computed(() => {
  return props.gambit?.pendingHandshakes || props.location?.pendingHandshakes || [];
});

let lastPendingIds: string[] = [];

watch(
  pendingHandshakes,
  (newPendingHandshakes) => {
    if (!authUser.value) return;

    const currentIds = (newPendingHandshakes || []).map((h) => h.id).sort();
    const idsChanged =
      currentIds.length !== lastPendingIds.length || currentIds.some((id, i) => id !== lastPendingIds[i]);

    if (currentHandshake.value && !currentHandshake.value.isConfirmed && isModalOpen.value) {
      const allHandshakes = [...(props.handshakes || []), ...(newPendingHandshakes || [])];
      if (!allHandshakes.some((h) => h.id === currentHandshake.value!.id)) {
        setModalOpen(false);
        currentHandshake.value = null;
        close();
        listEmit(GamComponentsEmits.UPDATE);
        lastPendingIds = currentIds;
        return;
      }
    }

    if (!idsChanged) {
      lastPendingIds = currentIds;
      return;
    }
    lastPendingIds = currentIds;

    if (isHandshakeModalOpen()) return;
    if (!newPendingHandshakes?.length) return;

    const incomingHandshake = newPendingHandshakes.find(
      (h) => !h.isConfirmed && h.responderId === authUser.value!.id && !wasHandshakeShown(h.id),
    );

    if (!incomingHandshake?.initiator) return;

    markHandshakeShown(incomingHandshake.id);
    handshakeAttendee.value = incomingHandshake.initiator;
    currentHandshake.value = incomingHandshake;

    setModalOpen(true);
    nextTick(() => {
      openHandshakeModal();
    });
  },
  { immediate: true },
);

const { fire: fireConfetti } = useConfetti();

watch(
  () => props.handshakes,
  (next, previous) => {
    celebrateNewlyConfirmedHandshakes(previous, next, authUser.value?.id, fireConfetti);
  },
);

function isOtherPlayer(id: string) {
  return id !== authUser.value?.id;
}

function canCreateHandshakeWith(player: { id: string }) {
  if (!props.location.isCheckedIn) return false;
  if (!isOtherPlayer(player.id)) return false;

  return true;
}

function canReturnHandshakeTo(player: { id: string }) {
  if (!isOtherPlayer(player.id)) return false;

  const handshake = getPendingHandshakeFor(player.id);
  if (!handshake) return false;

  return !handshake.isConfirmed;
}

const isStreamed = computed(() => props.gambit?.gambitType.includes(GambitType.STREAMED));
const isLesson = computed(() => props.gambit?.gambitCategory === GambitCategory.LESSON);
</script>

<style lang="scss" scoped>
.attendee-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 0.5rem;
  gap: var(--spacing-md);
}

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-xs);
}

.invitee {
  opacity: 0.33;
  pointer-events: none;
}

/* Flat, so the raised button beside it is the one thing that reads as the action. */
.attendee {
  flex-shrink: 1;
}

.spacer {
  width: 56px;
  height: 52px;
  flex-shrink: 0;
}
</style>
